// WO-33 — new-user onboarding: shared state for the welcome window, the
// checklist, the tour, the help menu and the helpful empty screens.
//
// Safety rules (Onboarding Spec §1):
//  R1  Everything hangs off ONE server switch. Until GET /api/onboarding/state
//      says {on: true}, `on` is false and every onboarding piece renders
//      nothing (empty screens render the page's own old content).
//  R3  Any failure — network, server, a bug in this code — leaves `on` false.
//      It is logged to the console, never shown to the customer.
//  R4  Progress is computed by the server from records that already exist.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pricingUrl } from './copy';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DONE_KEY = 'm2b:onboarding:done-session';

const OFF = Object.freeze({ on: false, eligible: false, tasks: [] });

export function onbWarn(...args) {
  // eslint-disable-next-line no-console
  console.warn('[onboarding]', ...args);
}

function readSession(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function writeSession(key, value) {
  try { sessionStorage.setItem(key, value); } catch { /* private mode: fine */ }
}

// Where each checklist task's "Go" button leads.
export const TASK_ROUTES = {
  A: '/dashboard/tenders',
  B: '/dashboard/tenders',
  C: '/dashboard/tenders',
  D: '/dashboard/companies',
  E: '/dashboard/reports',
  team: '/dashboard/group',
};

const OnboardingContext = createContext(null);

export function OnboardingProvider({ children }) {
  const { user, getToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [state, setState] = useState(OFF);
  const [expanded, setExpanded] = useState(false);   // checklist panel open (vs. pill)
  const [forced, setForced] = useState(false);       // reopened from the help menu
  const [tourActive, setTourActive] = useState(false);
  const inflight = useRef(null);
  const lastFetch = useRef(0);

  const refresh = useCallback(async () => {
    const token = getToken && getToken();
    if (!token) { setState(OFF); return OFF; }
    if (inflight.current) return inflight.current;
    const run = (async () => {
      try {
        const res = await fetch(`${API_URL}/api/onboarding/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          onbWarn('state request failed', res.status);
          setState(OFF);
          return OFF;
        }
        const data = await res.json();
        const next = data && data.on ? { ...OFF, ...data, tasks: data.tasks || [] } : OFF;
        if (next.completed_now && next.completed_at) writeSession(DONE_KEY, next.completed_at);
        setState(next);
        return next;
      } catch (e) {
        onbWarn('state request error', e);
        setState(OFF);
        return OFF;
      } finally {
        lastFetch.current = Date.now();
        inflight.current = null;
      }
    })();
    inflight.current = run;
    return run;
  }, [getToken]);

  const save = useCallback(async (patch) => {
    // Optimistic: the screen follows the user's choice at once; the server
    // copy is the one that counts on the next load.
    setState((s) => ({
      ...s,
      ...(patch.goal ? { goal: patch.goal } : {}),
      ...(patch.welcome_seen ? { welcome_seen: true } : {}),
      ...(patch.checklist_hidden !== undefined ? { checklist_hidden: patch.checklist_hidden } : {}),
    }));
    const token = getToken && getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/onboarding/me`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) onbWarn('save failed', res.status);
    } catch (e) {
      onbWarn('save error', e);
    }
    refresh();
  }, [getToken, refresh]);

  // Load on sign-in / account change.
  useEffect(() => {
    if (!user) { setState(OFF); return; }
    refresh();
  }, [user, refresh]);

  // While onboarding is on, re-read after navigating (throttled) and when the
  // tab regains focus: progress ticks, and switching onboarding OFF takes
  // effect on the next page change without a reload. While it is off, nothing
  // is re-read until the next sign-in or full page load.
  useEffect(() => {
    if (!state.on) return undefined;
    const t = setTimeout(() => {
      if (Date.now() - lastFetch.current > 4000) refresh();
    }, 900);
    return () => clearTimeout(t);
  }, [location.pathname, state.on, refresh]);

  useEffect(() => {
    if (!state.on) return undefined;
    const onFocus = () => { if (Date.now() - lastFetch.current > 4000) refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [state.on, refresh]);

  // ---- derived -------------------------------------------------------------
  const on = !!state.on;
  const eligible = on && !!state.eligible;
  const completedThisSession = !!state.completed_at && readSession(DONE_KEY) === state.completed_at;
  const finishedEarlier = !!state.completed_at && !completedThisSession;

  const showWelcome = eligible && !state.welcome_seen && !tourActive;
  const showChecklist = eligible && !!state.welcome_seen && !tourActive && (
    forced || (!state.checklist_hidden && !finishedEarlier)
  );
  const available = (state.tasks || []).filter((t) => !t.locked);
  const doneCount = available.filter((t) => t.done).length;

  // ---- actions --------------------------------------------------------------
  const chooseGoal = useCallback((goal, then) => {
    save({ ...(goal ? { goal } : {}), welcome_seen: true });
    if (then === 'tour') {
      setExpanded(false);
      setTourActive(true);
      navigate('/dashboard/tenders');
    } else if (then === 'checklist') {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  }, [save, navigate]);

  const startTour = useCallback(() => {
    setExpanded(false);
    setTourActive(true);
    navigate('/dashboard/tenders');
  }, [navigate]);

  const endTour = useCallback(() => setTourActive(false), []);

  const openChecklist = useCallback(() => {
    setForced(true);
    setExpanded(true);
    if (state.checklist_hidden) save({ checklist_hidden: false });
  }, [save, state.checklist_hidden]);

  const hideChecklist = useCallback(() => {
    setForced(false);
    setExpanded(false);
    save({ checklist_hidden: true });
  }, [save]);

  const goToTask = useCallback((task) => {
    setExpanded(false);
    if (task.locked) {
      window.location.href = pricingUrl('onboarding');
      return;
    }
    navigate(TASK_ROUTES[task.id] || '/dashboard');
  }, [navigate]);

  // Task E on the Free plan ticks when the reports catalogue is opened (spec §8).
  const markReportsOpened = useCallback(() => {
    if (!eligible || state.plan !== 'free') return;
    const e = (state.tasks || []).find((t) => t.id === 'E');
    if (!e || e.done) return;
    save({ opened_reports: true });
  }, [eligible, state.plan, state.tasks, save]);

  const value = useMemo(() => ({
    ...state, on, eligible, showWelcome, showChecklist, expanded, setExpanded,
    tourActive, doneCount, total: available.length, completedThisSession,
    refresh, save, chooseGoal, startTour, endTour, openChecklist, hideChecklist,
    goToTask, markReportsOpened,
  }), [state, on, eligible, showWelcome, showChecklist, expanded, tourActive, doneCount,
      available.length, completedThisSession, refresh, save, chooseGoal, startTour, endTour,
      openChecklist, hideChecklist, goToTask, markReportsOpened]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

const NOOP = () => {};
const OFF_VALUE = Object.freeze({
  ...OFF, showWelcome: false, showChecklist: false, expanded: false, setExpanded: NOOP,
  tourActive: false, doneCount: 0, total: 0, completedThisSession: false,
  refresh: NOOP, save: NOOP, chooseGoal: NOOP, startTour: NOOP, endTour: NOOP,
  openChecklist: NOOP, hideChecklist: NOOP, goToTask: NOOP, markReportsOpened: NOOP,
});

// Outside the provider (or before it loads) onboarding is simply OFF.
export function useOnboarding() {
  return useContext(OnboardingContext) || OFF_VALUE;
}

// Renders `fallback` (default: nothing) if anything inside throws, so a bug in
// onboarding can never take a page down (R3).
export class OnboardingBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err) {
    onbWarn('component error', err);
  }
  render() {
    if (this.state.failed) return this.props.fallback === undefined ? null : this.props.fallback;
    return this.props.children;
  }
}
